import type React from "react";
import { useState } from "react";
import Modal from "~/components/ui/modal";
import type {
  DeletedFieldInfo,
  InvalidReference,
  ValidationResult,
} from "~/utils/fieldDesignValidation";
import styles from "./index.module.scss";

interface ValidationWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** 保存を確認（致命的な参照がない場合、または削除確認済みの場合） */
  onConfirm: () => void;
  /** 致命的な参照のあるフィールドを削除して保存 */
  onConfirmWithDeletion: () => void;
  validationResult: ValidationResult;
}

const ValidationWarningModal: React.FC<ValidationWarningModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  onConfirmWithDeletion,
  validationResult,
}) => {
  const {
    deletedFields,
    criticalReferences,
    safeCleanableReferences,
    shouldBlockSave,
    blockingErrors,
  } = validationResult;
  const [deletionConfirmed, setDeletionConfirmed] = useState(false);

  const handleConfirm = () => {
    if (shouldBlockSave && deletionConfirmed) {
      onConfirmWithDeletion();
    } else if (!shouldBlockSave) {
      onConfirm();
    }
  };

  const renderDeletedFields = () => {
    if (deletedFields.length === 0) return null;

    return (
      <div className={styles["section"]}>
        <h3 className={styles["sectionTitle"]}>
          <span className={styles["warningIcon"]}>⚠</span>
          削除されるフィールド ({deletedFields.length}件)
        </h3>
        <ul className={styles["list"]}>
          {deletedFields.map((field: DeletedFieldInfo, index: number) => (
            <li key={index} className={styles["listItem"]}>
              <span className={styles["fieldName"]}>{field.fieldName}</span>
              <span className={styles["fieldCode"]}>({field.fieldCode})</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderCriticalReferences = () => {
    if (criticalReferences.length === 0) return null;

    return (
      <div className={`${styles["section"]} ${styles["criticalSection"]}`}>
        <h3 className={styles["sectionTitle"]}>
          <span className={styles["errorIcon"]}>✕</span>
          保存できない参照フィールド ({criticalReferences.length}件)
        </h3>
        <p className={styles["description"]}>
          以下のフィールドは参照キーが削除されたため無効です。
          <strong>保存するにはこれらのフィールドを削除する必要があります。</strong>
        </p>
        <ul className={styles["list"]}>
          {criticalReferences.map((ref: InvalidReference, index: number) => (
            <li key={index} className={`${styles["listItem"]} ${styles["criticalItem"]}`}>
              <div className={styles["referenceInfo"]}>
                <span className={styles["referenceType"]}>
                  {ref.referenceType === "LOOKUP" ? "ルックアップ" : "関連レコード"}
                </span>
                <span className={styles["fieldName"]}>{ref.sourceField.フィールド名}</span>
              </div>
              <div className={styles["criticalReason"]}>{ref.criticalReason}</div>
            </li>
          ))}
        </ul>
        <div className={styles["confirmDeletion"]}>
          <label className={styles["checkboxLabel"]}>
            <input
              type="checkbox"
              checked={deletionConfirmed}
              onChange={(e) => setDeletionConfirmed(e.target.checked)}
            />
            上記のフィールドを削除することを確認しました
          </label>
        </div>
      </div>
    );
  };

  const renderSafeCleanableReferences = () => {
    if (safeCleanableReferences.length === 0) return null;

    return (
      <div className={styles["section"]}>
        <h3 className={styles["sectionTitle"]}>
          <span className={styles["infoIcon"]}>ℹ</span>
          クリアされる参照設定 ({safeCleanableReferences.length}件)
        </h3>
        <p className={styles["description"]}>
          以下の参照設定は、参照先フィールドが削除されたため一部がクリアされます。
          フィールド自体は保持されます。
        </p>
        <ul className={styles["list"]}>
          {safeCleanableReferences.map((ref: InvalidReference, index: number) => (
            <li key={index} className={styles["listItem"]}>
              <div className={styles["referenceInfo"]}>
                <span className={styles["referenceType"]}>
                  {ref.referenceType === "LOOKUP" ? "ルックアップ" : "関連レコード"}
                </span>
                <span className={styles["fieldName"]}>{ref.sourceField.フィールド名}</span>
              </div>
              <div className={styles["referenceDetail"]}>
                クリア対象: {ref.deletedFieldCodes.join(", ")}
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderBlockingErrors = () => {
    if (!blockingErrors || blockingErrors.length === 0) return null;

    return (
      <div className={`${styles["section"]} ${styles["criticalSection"]}`}>
        <h3 className={styles["sectionTitle"]}>
          <span className={styles["errorIcon"]}>✕</span>
          保存できない設定 ({blockingErrors.length}件)
        </h3>
        <p className={styles["description"]}>
          以下の設定はアプリ作成時にエラーになります。修正してから保存してください。
        </p>
        <ul className={styles["list"]}>
          {blockingErrors.map((msg: string, index: number) => (
            <li key={index} className={`${styles["listItem"]} ${styles["criticalItem"]}`}>
              {msg}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const hasBlockingConfigErrors = (blockingErrors || []).length > 0;
  const canSave = !hasBlockingConfigErrors && (!shouldBlockSave || deletionConfirmed);

  const content = (
    <div className={styles["content"]}>
      <p className={styles["mainMessage"]}>
        {hasBlockingConfigErrors
          ? "保存するには以下の設定エラーを修正する必要があります。"
          : shouldBlockSave
            ? "保存するには以下の問題を解決する必要があります。"
            : "以下の変更を保存しますか？"}
      </p>

      {renderBlockingErrors()}
      {renderCriticalReferences()}
      {renderDeletedFields()}
      {renderSafeCleanableReferences()}

      <div className={styles["buttonContainer"]}>
        <button type="button" className={styles["cancelButton"]} onClick={onClose}>
          キャンセル
        </button>
        <button
          type="button"
          className={`${styles["confirmButton"]} ${!canSave ? styles["disabled"] : ""}`}
          onClick={handleConfirm}
          disabled={!canSave}
        >
          {hasBlockingConfigErrors
            ? "修正してください"
            : shouldBlockSave
              ? "フィールドを削除して保存"
              : "保存する"}
        </button>
      </div>
    </div>
  );

  return (
    <Modal
      label="保存の確認"
      isOpen={isOpen}
      onClose={onClose}
      content={content}
      className="validationWarningModal"
      usePortal={true}
    />
  );
};

export default ValidationWarningModal;
